// CurrentWeather.js
import React from "react";
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  alpha,
} from "@mui/material";
import { useTheme, styled } from "@mui/material/styles";
import {
  getWindDirectionIcon,
  getWindDirectionText,
} from "./utils/weatherIcons";
import { getMoonPhase } from "./utils/weatherUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import OpacityIcon from "@mui/icons-material/Opacity";
import AirIcon from "@mui/icons-material/Air";
import CloudIcon from "@mui/icons-material/Cloud";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import VisibilityIcon from "@mui/icons-material/Visibility";
import UmbrellaIcon from "@mui/icons-material/Umbrella";
import NightsStayIcon from "@mui/icons-material/NightsStay";
import { useUnits } from "../../contexts/UnitContext";

// Glassmorphic Main Card
const GlassCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.8)} 0%, 
    ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
  backdropFilter: "blur(20px) saturate(1.1)",
  WebkitBackdropFilter: "blur(20px) saturate(1.1)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
  borderRadius: 20,
  position: "relative",
  overflow: "hidden",
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.06)}`,
  marginBottom: theme.spacing(3),

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "2px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.primary.main, 0.4)}, 
      ${alpha(theme.palette.secondary.main, 0.3)}, 
      transparent)`,
    borderRadius: "20px 20px 0 0",
    zIndex: 1,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 20% 20%, 
      ${alpha(theme.palette.primary.main, 0.03)} 0%, 
      transparent 50%), 
      radial-gradient(circle at 80% 80%, 
      ${alpha(theme.palette.secondary.main, 0.03)} 0%, 
      transparent 50%)`,
    pointerEvents: "none",
    zIndex: 0,
  },

  "& > *": {
    position: "relative",
    zIndex: 2,
  },
}));

// Glassmorphic Sub Card
const GlassSubCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.4)} 0%, 
    ${alpha(theme.palette.background.paper, 0.2)} 100%)`,
  backdropFilter: "blur(15px) saturate(1.05)",
  WebkitBackdropFilter: "blur(15px) saturate(1.05)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
  borderRadius: 16,
  position: "relative",
  overflow: "hidden",
  boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.04)}`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.primary.main, 0.2)}, 
      ${alpha(theme.palette.secondary.main, 0.15)}, 
      transparent)`,
    borderRadius: "16px 16px 0 0",
    zIndex: 1,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 30% 30%, 
      ${alpha(theme.palette.primary.main, 0.02)} 0%, 
      transparent 60%)`,
    pointerEvents: "none",
    zIndex: 0,
  },

  "& > *": {
    position: "relative",
    zIndex: 2,
  },

  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.background.paper, 0.5)} 0%, 
      ${alpha(theme.palette.background.paper, 0.3)} 100%)`,
    borderColor: alpha(theme.palette.primary.main, 0.15),

    "&::before": {
      background: `linear-gradient(90deg, 
        transparent, 
        ${alpha(theme.palette.primary.main, 0.3)}, 
        ${alpha(theme.palette.secondary.main, 0.25)}, 
        transparent)`,
    },
  },
}));

// Glassmorphic Chip
const GlassChip = styled(Chip)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.primary.main, 0.08)} 0%, 
    ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
  backdropFilter: "blur(8px)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  color: theme.palette.primary.main,
  fontWeight: 500,
  borderRadius: 12,

  "&:hover": {
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.primary.main, 0.12)} 0%, 
      ${alpha(theme.palette.primary.main, 0.06)} 100%)`,
  },

  "& .MuiChip-icon": {
    color: theme.palette.primary.main,
    filter: `drop-shadow(0 2px 4px ${alpha(theme.palette.primary.main, 0.3)})`,
  },
}));

// Glassmorphic Weather Icon Container
const WeatherIconContainer = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.primary.main, 0.06)} 0%, 
    ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  borderRadius: 16,
  padding: theme.spacing(1),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at center, 
      ${alpha(theme.palette.common.white, 0.1)} 0%, 
      transparent 70%)`,
    borderRadius: "inherit",
    pointerEvents: "none",
  },
}));

const CurrentWeather = ({ weatherData, lastUpdated }) => {
  const theme = useTheme();
  const { unitSystem, timeFormat, convert } = useUnits();

  if (!weatherData) {
    return (
      <GlassCard>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="body1" color="text.secondary" textAlign="center">
            No current weather data available.
          </Typography>
        </CardContent>
      </GlassCard>
    );
  }

  // Convert temperature based on unit preference
  const formatTemperature = (celsius) => {
    if (unitSystem === "metric") {
      return `${Math.round(celsius)}°C`;
    } else {
      // Convert to Fahrenheit
      const fahrenheit = (celsius * 9) / 5 + 32;
      return `${Math.round(fahrenheit)}°F`;
    }
  };

  // Convert wind speed based on unit preference
  const formatWindSpeed = (metersPerSecond) => {
    if (unitSystem === "metric") {
      // Keep as m/s for metric
      return `${metersPerSecond.toFixed(1)} m/s`;
    } else {
      // Convert to mph for imperial
      const mph = metersPerSecond * 2.23694;
      return `${mph.toFixed(1)} mph`;
    }
  };

  // Convert visibility based on unit preference
  const formatVisibility = (meters) => {
    if (unitSystem === "metric") {
      // Convert to kilometers for metric
      return `${(meters / 1000).toFixed(1)} km`;
    } else {
      // Convert to miles for imperial
      const miles = meters * 0.000621371;
      return `${miles.toFixed(1)} mi`;
    }
  };

  // Format time using the UnitContext
  const formatTime = (timestamp) => {
    return convert.time.format(new Date(timestamp * 1000));
  };

  return (
    <GlassCard>
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <GlassChip
            icon={<AccessTimeIcon fontSize="small" />}
            label={`Last Updated: ${convert.time.formatDate(
              lastUpdated,
              true
            )}`}
            size="small"
            variant="outlined"
          />
        </Box>

        {/* Main content grid */}
        <Grid container spacing={3}>
          {/* Left column */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={3}>
              {/* Weather conditions */}
              <Grid item xs={12}>
                <GlassSubCard>
                  <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        color="primary"
                        sx={{
                          fontWeight: "bold",
                          filter: `drop-shadow(0 2px 4px ${alpha(
                            theme.palette.primary.main,
                            0.2
                          )})`,
                        }}
                      >
                        Conditions
                      </Typography>
                      {weatherData.weather && weatherData.weather[0] && (
                        <Typography
                          variant="body1"
                          color="text.primary"
                          sx={{
                            textTransform: "capitalize",
                            fontWeight: 500,
                          }}
                        >
                          {weatherData.weather[0].description}
                        </Typography>
                      )}
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      {weatherData.weather &&
                        weatherData.weather.map((item, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              width: "100%",
                            }}
                          >
                            <WeatherIconContainer>
                              <img
                                src={
                                  item.iconUrl ||
                                  `http://openweathermap.org/img/wn/${item.icon}@2x.png`
                                }
                                alt={item.description}
                                style={{ width: "60px", height: "60px" }}
                              />
                            </WeatherIconContainer>
                            <Box sx={{ ml: 3 }}>
                              <Typography
                                variant="h3"
                                sx={{
                                  lineHeight: 1.1,
                                  fontWeight: "bold",
                                  background: `linear-gradient(135deg, 
                                    ${theme.palette.primary.main} 0%, 
                                    ${theme.palette.secondary.main} 100%)`,
                                  WebkitBackgroundClip: "text",
                                  WebkitTextFillColor: "transparent",
                                  backgroundClip: "text",
                                }}
                              >
                                {formatTemperature(weatherData.temp)}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                              >
                                Feels like:{" "}
                                {formatTemperature(weatherData.feels_like)}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                    </Box>
                  </CardContent>
                </GlassSubCard>
              </Grid>

              {/* Temperature details and humidity */}
              <Grid item xs={12}>
                <GlassSubCard>
                  <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                    <Typography
                      variant="subtitle1"
                      color="primary"
                      gutterBottom
                      sx={{
                        fontWeight: "bold",
                        mb: 2,
                        filter: `drop-shadow(0 2px 4px ${alpha(
                          theme.palette.primary.main,
                          0.2
                        )})`,
                      }}
                    >
                      Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 2 }}
                        >
                          <OpacityIcon
                            color="primary"
                            fontSize="small"
                            sx={{
                              mr: 1.5,
                              filter: `drop-shadow(0 2px 4px ${alpha(
                                theme.palette.primary.main,
                                0.3
                              )})`,
                            }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Humidity: {weatherData.humidity}%
                          </Typography>
                        </Box>
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 2 }}
                        >
                          <AirIcon
                            color="primary"
                            fontSize="small"
                            sx={{
                              mr: 1.5,
                              filter: `drop-shadow(0 2px 4px ${alpha(
                                theme.palette.primary.main,
                                0.3
                              )})`,
                            }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Pressure: {weatherData.pressure} hPa
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 2 }}
                        >
                          <CloudIcon
                            color="primary"
                            fontSize="small"
                            sx={{
                              mr: 1.5,
                              filter: `drop-shadow(0 2px 4px ${alpha(
                                theme.palette.primary.main,
                                0.3
                              )})`,
                            }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Clouds: {weatherData.clouds}%
                          </Typography>
                        </Box>
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 2 }}
                        >
                          <VisibilityIcon
                            color="primary"
                            fontSize="small"
                            sx={{
                              mr: 1.5,
                              filter: `drop-shadow(0 2px 4px ${alpha(
                                theme.palette.primary.main,
                                0.3
                              )})`,
                            }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Air Visibility:{" "}
                            {formatVisibility(weatherData.visibility)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </GlassSubCard>
              </Grid>
            </Grid>
          </Grid>

          {/* Right column */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={3}>
              {/* Wind */}
              <Grid item xs={12}>
                <GlassSubCard>
                  <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                    <Typography
                      variant="subtitle1"
                      color="primary"
                      gutterBottom
                      sx={{
                        fontWeight: "bold",
                        mb: 2,
                        filter: `drop-shadow(0 2px 4px ${alpha(
                          theme.palette.primary.main,
                          0.2
                        )})`,
                      }}
                    >
                      Wind
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box
                          sx={{
                            mr: 2,
                            p: 1,
                            borderRadius: 2,
                            background: `linear-gradient(135deg, 
                              ${alpha(theme.palette.primary.main, 0.06)} 0%, 
                              ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
                            border: `1px solid ${alpha(
                              theme.palette.primary.main,
                              0.15
                            )}`,
                          }}
                        >
                          <FontAwesomeIcon
                            icon={getWindDirectionIcon(weatherData.wind_deg)}
                            style={{
                              color: theme.palette.primary.main,
                              filter: `drop-shadow(0 2px 4px ${alpha(
                                theme.palette.primary.main,
                                0.3
                              )})`,
                            }}
                            size="lg"
                          />
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getWindDirectionText(weatherData.wind_deg)}
                        </Typography>
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: "bold",
                          color: "primary.main",
                        }}
                      >
                        {formatWindSpeed(weatherData.wind_speed)}
                      </Typography>
                    </Box>

                    {/* Additional weather data */}
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <WbSunnyIcon
                            color="primary"
                            fontSize="small"
                            sx={{
                              mr: 1.5,
                              filter: `drop-shadow(0 2px 4px ${alpha(
                                theme.palette.primary.main,
                                0.3
                              )})`,
                            }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            UV Index: {weatherData.uvi}
                          </Typography>
                        </Box>
                      </Grid>
                      {weatherData.rain && (
                        <Grid item xs={6}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <UmbrellaIcon
                              color="primary"
                              fontSize="small"
                              sx={{
                                mr: 1.5,
                                filter: `drop-shadow(0 2px 4px ${alpha(
                                  theme.palette.primary.main,
                                  0.3
                                )})`,
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              Rain: {weatherData.rain["1h"]} mm
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </GlassSubCard>
              </Grid>

              {/* Sun and Moon */}
              <Grid item xs={12}>
                <GlassSubCard>
                  <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                    <Grid container spacing={3}>
                      <Grid item xs={6}>
                        <Typography
                          variant="subtitle1"
                          color="primary"
                          gutterBottom
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            fontWeight: "bold",
                            mb: 2,
                            filter: `drop-shadow(0 2px 4px ${alpha(
                              theme.palette.primary.main,
                              0.2
                            )})`,
                          }}
                        >
                          <WbSunnyIcon
                            sx={{
                              mr: 1,
                              filter: `drop-shadow(0 2px 4px ${alpha(
                                theme.palette.primary.main,
                                0.3
                              )})`,
                            }}
                            fontSize="small"
                          />
                          Sun
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 1.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mr: 1, minWidth: 40 }}
                          >
                            Rise:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatTime(weatherData.sunrise)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mr: 1, minWidth: 40 }}
                          >
                            Set:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatTime(weatherData.sunset)}
                          </Typography>
                        </Box>
                      </Grid>

                      {weatherData.moon_phase !== undefined &&
                        !isNaN(weatherData.moon_phase) && (
                          <Grid item xs={6}>
                            <Typography
                              variant="subtitle1"
                              color="primary"
                              gutterBottom
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                fontWeight: "bold",
                                mb: 2,
                                filter: `drop-shadow(0 2px 4px ${alpha(
                                  theme.palette.primary.main,
                                  0.2
                                )})`,
                              }}
                            >
                              <NightsStayIcon
                                sx={{
                                  mr: 1,
                                  filter: `drop-shadow(0 2px 4px ${alpha(
                                    theme.palette.primary.main,
                                    0.3
                                  )})`,
                                }}
                                fontSize="small"
                              />
                              Moon
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {getMoonPhase(weatherData.moon_phase)}
                            </Typography>
                          </Grid>
                        )}
                    </Grid>
                  </CardContent>
                </GlassSubCard>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </GlassCard>
  );
};

export default CurrentWeather;
